import { useState, useRef, useEffect, useCallback } from 'react';
import { searchAirports, getCityName, type AirportEntry } from '../../lib/airports';

interface AirportAutocompleteProps {
  label: string;
  value: string;
  onChange: (code: string) => void;
  placeholder?: string;
  required?: boolean;
}

/**
 * Airport code input with dropdown autocomplete.
 * Matches by IATA code prefix or city name. Dropdown items show
 * code in mono + city name. Selecting sets the 3-letter code.
 */
export function AirportAutocomplete({
  label,
  value,
  onChange,
  placeholder = 'SFO',
  required,
}: AirportAutocompleteProps) {
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState<AirportEntry[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Sync external value changes
  useEffect(() => {
    setQuery(value);
  }, [value]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.toUpperCase();
    setQuery(val);
    setActiveIndex(-1);

    if (val.length > 0) {
      const matches = searchAirports(val);
      setResults(matches);
      setIsOpen(matches.length > 0);
    } else {
      setResults([]);
      setIsOpen(false);
      onChange('');
    }
  }, [onChange]);

  const selectAirport = useCallback((airport: AirportEntry) => {
    setQuery(airport.code);
    onChange(airport.code);
    setIsOpen(false);
    setActiveIndex(-1);
  }, [onChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < results.length) {
          selectAirport(results[activeIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setActiveIndex(-1);
        break;
    }
  }, [isOpen, results, activeIndex, selectAirport]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // On blur: if query is a valid 3-letter code, commit it
  const handleBlur = useCallback(() => {
    const trimmed = query.trim().toUpperCase();
    if (trimmed.length === 3 && getCityName(trimmed)) {
      onChange(trimmed);
      setQuery(trimmed);
    }
    // Delay close so click on dropdown item registers
    setTimeout(() => setIsOpen(false), 150);
  }, [query, onChange]);

  const cityName = getCityName(value);

  return (
    <div className="w-full relative">
      <label className="block mb-2 text-sm font-medium text-stone-700 dark:text-stone-300">
        {label}
        {required && <span className="text-primary ml-1">*</span>}
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (query.length > 0) {
              const matches = searchAirports(query);
              setResults(matches);
              setIsOpen(matches.length > 0);
            }
          }}
          onBlur={handleBlur}
          placeholder={placeholder}
          maxLength={3}
          required={required}
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          aria-controls="airport-listbox"
          className="w-full px-4 py-2.5 bg-white dark:bg-stone-800 border border-stone-300 dark:border-stone-600 rounded-lg text-stone-900 dark:text-stone-100 font-mono font-semibold tracking-tight placeholder:text-stone-400 dark:placeholder:text-stone-500 placeholder:font-normal placeholder:tracking-normal focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
        />
        {cityName && value.length === 3 && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-stone-400 dark:text-stone-500 pointer-events-none">
            {cityName}
          </span>
        )}
      </div>

      {isOpen && results.length > 0 && (
        <div
          ref={dropdownRef}
          id="airport-listbox"
          role="listbox"
          className="absolute z-50 mt-1 w-full max-h-48 overflow-auto bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg shadow-lg"
        >
          {results.map((airport, i) => (
            <button
              key={airport.code}
              role="option"
              aria-selected={i === activeIndex}
              onMouseDown={(e) => {
                e.preventDefault();
                selectAirport(airport);
              }}
              className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors ${
                i === activeIndex
                  ? 'bg-stone-100 dark:bg-stone-700'
                  : 'hover:bg-stone-50 dark:hover:bg-stone-700/50'
              }`}
            >
              <span className="font-mono font-semibold text-stone-900 dark:text-stone-100 tracking-tight">
                {airport.code}
              </span>
              <span className="text-sm text-stone-500 dark:text-stone-400">
                {airport.city}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
